<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import MasifiWhatsappQrPanel from '../masifi/MasifiWhatsappQrPanel.vue';
import {
  WHATSAPP_QR_FEATURE,
  whatsappQrLinkPath,
} from 'dashboard/helper/masifiWhatsappQr';

// Alta de un inbox "WhatsApp (código QR)": capa 2 del gate (sin el módulo, ni el form se muestra —
// la API respondería 403 igual, pero acá se explica por qué) + form nativo (vuelidate, mismo patrón
// que CloudWhatsapp.vue) + panel de vinculación en vivo apenas se crea el inbox. Spec: whatsapp-qr-design.md §9.
const { t } = useI18n();
const store = useStore();
const { isCloudFeatureEnabled } = useAccount();

const moduleEnabled = computed(() =>
  isCloudFeatureEnabled(WHATSAPP_QR_FEATURE)
);

const inboxName = ref('');
const phoneNumber = ref('');
const receiveGroups = ref(true); // G6: encendido por defecto
const createdInbox = ref(null);
const linked = ref(false);

const rules = {
  inboxName: { required },
  phoneNumber: { required, isPhoneE164OrEmpty },
};
const v$ = useVuelidate(rules, { inboxName, phoneNumber });

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    createdInbox.value = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value?.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumber.value,
        provider: 'whatsapp_qr',
        provider_config: { groups: receiveGroups.value },
      },
    });
  } catch (error) {
    useAlert(error.message || t('MASIFI_WHATSAPP_QR.ERRORS.GENERIC'));
  }
};

// UX fix (Marcos 2026-09-23): "link later" is a secondary action, so it says what it leaves pending and where
// to finish it (path built from the real sidebar/tab labels, never hardcoded).
const laterHelp = computed(() =>
  t('MASIFI_WHATSAPP_QR.LATER_HELP', {
    path: whatsappQrLinkPath(t, createdInbox.value?.name),
    later: t('MASIFI_WHATSAPP_QR.CONTINUE_LATER'),
  })
);

const goToAddAgents = () => {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: createdInbox.value.id },
  });
};
</script>

<template>
  <p v-if="!moduleEnabled" class="text-sm text-n-slate-11">
    {{ t('MASIFI_WHATSAPP_QR.NOT_AVAILABLE') }}
  </p>
  <div v-else-if="!createdInbox" class="flex flex-col mx-0">
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.phoneNumber.$error }">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
          <!-- E2E fix #6 (Marcos, live test 2026-09-23): native PhoneNumberInput (country selector, same
          component ContactsForm uses) instead of a raw text input. It always emits E.164, so the value sent
          to the API is unchanged; the default country comes from the browser's timezone (no hardcoded Ecuador). -->
          <PhoneNumberInput
            v-model="phoneNumber"
            :placeholder="t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          />
          <span v-if="v$.phoneNumber.$error" class="message">
            {{ t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
          </span>
        </label>
      </div>

      <SettingsToggleSection
        v-model="receiveGroups"
        :header="t('MASIFI_WHATSAPP_QR.SETTINGS.RECEIVE_GROUPS')"
        :description="t('MASIFI_WHATSAPP_QR.SETTINGS.RECEIVE_GROUPS_HELP')"
      />

      <div class="w-full mt-4">
        <NextButton
          type="submit"
          solid
          blue
          :label="t('MASIFI_WHATSAPP_QR.CONNECT')"
        />
      </div>
    </form>
  </div>
  <div v-else class="flex flex-col gap-4">
    <MasifiWhatsappQrPanel :inbox="createdInbox" @connected="linked = true" />
    <!-- UX fix (Marcos 2026-09-23): once linked, one primary "Continue" + a success line; until then the
    panel's own "Generate QR" is the primary action and "link later" is a secondary link that explains itself. -->
    <div v-if="linked" class="flex flex-col gap-3">
      <p data-test="wa-qr-linked-help" class="text-sm text-n-teal-11">
        {{ t('MASIFI_WHATSAPP_QR.LINKED_HELP') }}
      </p>
      <div>
        <NextButton
          data-test="wa-qr-continue"
          :label="t('MASIFI_WHATSAPP_QR.CONTINUE')"
          @click="goToAddAgents"
        />
      </div>
    </div>
    <div v-else class="flex flex-col items-start gap-2">
      <p data-test="wa-qr-later-help" class="text-sm text-n-slate-11">
        {{ laterHelp }}
      </p>
      <NextButton
        data-test="wa-qr-link-later"
        variant="link"
        color="slate"
        :label="t('MASIFI_WHATSAPP_QR.CONTINUE_LATER')"
        @click="goToAddAgents"
      />
    </div>
  </div>
</template>
